import { ReactNode } from 'react'

import {
  Event,
  RecurringEventRegistrationMode,
} from '@tribeplatform/gql-client/types'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Divider } from '@tribeplatform/react-ui-kit/Divider'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Text } from '@tribeplatform/react-ui-kit/Text'

import { Image } from '../../common/components/Image.js'
import { useLogin } from '../../common/index.js'
import { dayjs } from '../../common/lib/dayjs.js'
import { T } from '../../i18n/index.js'
import { useEventActions } from '../hooks/useEventActions.js'
import { CalendarProvider } from '../types.js'

import {
  getCalendarProviderIconHref,
  hasRegisteredForEvent,
  isRecurringEvent,
} from './utils.js'

export const ActionButtons = ({ event }: { event: Event }) => {
  const atCapacity =
    !!event?.capacity && event?.registrationsCount >= event?.capacity
  const completed = dayjs(event?.endTime).isBefore(dayjs())
  const hasRegistered = hasRegisteredForEvent(event)

  if (completed) {
    return (
      <NonRegisterableEventButton
        text={
          <T
            id="Event.ActionButtons.PastEvent.HelperText"
            defaultMessage="Event has ended"
          />
        }
      />
    )
  }

  if (hasRegistered) {
    return <CancelRegistrationButton event={event} />
  }

  if (atCapacity) {
    return (
      <NonRegisterableEventButton
        text={
          <T
            id="Event.ActionButtons.AtCapacity.HelperText"
            defaultMessage="Reached maximum capacity"
          />
        }
      />
    )
  }

  if (!hasRegistered) {
    return <RegisterButton event={event} />
  }

  return null
}

const RegisterButton = ({ event }: { event: Event }) => {
  const { registerForEvent, isRegistering } = useEventActions()
  const { isLoggedIn, showLogin } = useLogin()
  const isRecurring = isRecurringEvent(event)

  return (
    <div className="flex flex-col gap-y-2 text-center w-full">
      <Text className="text-content-subdued">
        <T
          id="Event.ActionButtons.SaveYourSpot.Title"
          defaultMessage="Save your spot"
        />
      </Text>
      {!isLoggedIn && (
        <Button fullWidth onClick={() => showLogin()}>
          <T
            id="Event.ActionButtons.SaveYourSpot.Button"
            defaultMessage="RSVP now"
          />
        </Button>
      )}
      {isLoggedIn && isRecurring && (
        <RecurringEventRSVPButton
          button={
            <Button fullWidth>
              <T
                id="Event.ActionButtons.SaveYourSpot.Button"
                defaultMessage="RSVP now"
              />
            </Button>
          }
          title={
            <T
              id="Event.ActionButtons.RSVP.RecurringEvent.Title"
              defaultMessage="RSVP for event"
            />
          }
          description={
            <T
              id="Event.ActionButtons.RSVP.RecurringEvent.Description"
              defaultMessage="Which event would you like to RSVP for?"
            />
          }
          onSelect={mode => registerForEvent(event.id, mode)}
        />
      )}

      {isLoggedIn && !isRecurring && (
        <Button
          variant="primary"
          size="lg"
          fullWidth
          onClick={() => registerForEvent(event.id)}
          loading={isRegistering}
        >
          <T
            id="Event.ActionButtons.SaveYourSpot.Button"
            defaultMessage="RSVP now"
          />
        </Button>
      )}
    </div>
  )
}

const CancelRegistrationButton = ({ event }: { event: Event }) => {
  const {
    addToCalendar,
    cancelEventRegistration,
    isCancelling,
    downloadICSFile,
  } = useEventActions()
  const handleAddToCalendar = (provider: CalendarProvider) => {
    addToCalendar(event.id, provider)
  }

  const isRecurring = isRecurringEvent(event)

  return (
    <div className="flex flex-col gap-y-2 w-full">
      <Dropdown className="w-full" placement="bottom">
        <Dropdown.ButtonMinimal as="div">
          <Button leadingIcon={<SvgIcon name="calendar" />} fullWidth>
            <T
              id="Event.ActionButtons.AddToCalendar.Button"
              defaultMessage="Add to calendar"
            />
          </Button>
        </Dropdown.ButtonMinimal>
        <Dropdown.Items>
          <Dropdown.Item
            onClick={() => handleAddToCalendar(CalendarProvider.Google)}
            leadingIcon={
              <Image
                src={getCalendarProviderIconHref(CalendarProvider.Google)}
                alt="Google"
                className="!size-6"
              />
            }
          >
            <T
              id="Event.ActionButtons.AddToCalendar.Option.Google"
              defaultMessage="Google"
            />
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => handleAddToCalendar(CalendarProvider.Outlook)}
            leadingIcon={
              <Image
                src={getCalendarProviderIconHref(CalendarProvider.Outlook)}
                alt="Outlook"
                className="!size-6"
              />
            }
          >
            <T
              id="Event.ActionButtons.AddToCalendar.Option.Outlook"
              defaultMessage="Outlook"
            />
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => downloadICSFile(event.id)}
            leadingIcon={<SvgIcon name="download" />}
          >
            <T
              id="Event.ActionButtons.AddToCalendar.Option.DownloadICS"
              defaultMessage="Download ICS file"
            />
          </Dropdown.Item>
        </Dropdown.Items>
      </Dropdown>
      {!isRecurring && (
        <Button
          variant="tertiaryNeutral"
          size="lg"
          fullWidth
          loading={isCancelling}
          onClick={() => cancelEventRegistration(event.id)}
          className="underline text-content-muted"
        >
          <T
            id="Event.ActionButtons.CantMakeIt.Button"
            defaultMessage="Can't make it? Cancel registration"
          />
        </Button>
      )}
      {isRecurring && (
        <RecurringEventRSVPButton
          button={
            <Button
              fullWidth
              loading={isCancelling}
              variant="tertiaryNeutral"
              className="underline text-content-muted"
            >
              <T
                id="Event.ActionButtons.CantMakeIt.Button"
                defaultMessage="Can't make it? Cancel registration"
              />
            </Button>
          }
          title={
            <T
              id="Event.ActionButtons.RecurringEvent.CancelRegistration.Title"
              defaultMessage="Cancel registration"
            />
          }
          description={
            <T
              id="Event.ActionButtons.RecurringEvent.CancelRegistration.Description"
              defaultMessage="Which event would you like to cancel registration for?"
            />
          }
          onSelect={mode => cancelEventRegistration(event.id, mode)}
        />
      )}
    </div>
  )
}

const NonRegisterableEventButton = ({ text }: { text?: ReactNode }) => {
  return (
    <Text className="text-sm text-content-muted text-center w-full">
      {text}
    </Text>
  )
}

interface RecurringEventRSVPButtonProps {
  button: ReactNode
  title: ReactNode
  description?: ReactNode
  onSelect?: (mode: RecurringEventRegistrationMode) => void
}

const RecurringEventRSVPButton = ({
  button,
  onSelect,
  title,
  description,
}: RecurringEventRSVPButtonProps) => {
  return (
    <Dropdown placement="top-start">
      <Dropdown.ButtonMinimal as="div">{button}</Dropdown.ButtonMinimal>
      <Dropdown.Items>
        <div className="flex flex-col gap-2 p-2">
          <div className="flex items-center gap-2">
            <SvgIcon name="calendar" className="text-content-subdued" />
            <Text className="font-medium">{title}</Text>
          </div>
          <Text className="text-content-muted">{description}</Text>
          <Divider padding="sm" />
        </div>
        <Dropdown.Item
          leadingIcon={
            <SvgIcon name="calendar" className="text-content-muted" />
          }
          onClick={() =>
            onSelect?.(RecurringEventRegistrationMode.thisEventOnly)
          }
          className="!items-start"
        >
          <Text className="font-medium">
            <T
              id="Event.RecurringEvent.Actions.ThisEvent"
              defaultMessage="Only this event"
            />
          </Text>
        </Dropdown.Item>
        <Dropdown.Item
          leadingIcon={
            <SvgIcon name="refresh" className="text-content-muted" />
          }
          onClick={() =>
            onSelect?.(RecurringEventRegistrationMode.allUpcomingEvents)
          }
          className="!items-start"
        >
          <Text className="font-medium">
            <T
              id="Event.RecurringEvent.Actions.ThisAndFollowingEvents"
              defaultMessage="This and following events"
            />
          </Text>
        </Dropdown.Item>
      </Dropdown.Items>
    </Dropdown>
  )
}
